import { FILTER_OPERATORS } from 'src/constants/queries'
import type {
  BlueprintNode,
  FilterOperator,
  OperationType,
  SelectColumn,
  SelectColumnConfig
} from 'src/types/queries'
import { CellType } from 'src/types/tableDataSet'

const EQUALITY_ONLY: readonly FilterOperator[] = ['=', '!=']

const FILTER_OPERATORS_BY_TYPE: Record<CellType, readonly FilterOperator[]> = {
  string: FILTER_OPERATORS,
  number: FILTER_OPERATORS,
  date: FILTER_OPERATORS,
  time: FILTER_OPERATORS,
  datetime: FILTER_OPERATORS,
  bool: EQUALITY_ONLY,
  json: EQUALITY_ONLY
}

export const getFilterOperatorsForType = (
  uiType: CellType | undefined
): readonly FilterOperator[] =>
  uiType ? FILTER_OPERATORS_BY_TYPE[uiType] : FILTER_OPERATORS

export const hasBlueprintChanged = (
  original: BlueprintNode[] | undefined | null,
  current: BlueprintNode[] | undefined | null
): boolean => {
  if (!original?.length && !current?.length) {
    return false
  }
  if (original?.length !== current?.length) {
    return true
  }
  return JSON.stringify(original) !== JSON.stringify(current)
}

export const flattenPipeline = (node: BlueprintNode): BlueprintNode[] => {
  if (node.type === 'dataset') {
    return [node]
  }
  if (node.type === 'join') {
    return [node, ...flattenPipeline(node.left), ...flattenPipeline(node.right)]
  }
  return [node, ...flattenPipeline(node.source)]
}

export const ALIAS_REGEX = /^[a-zA-Z_][a-zA-Z0-9_]{0,62}$/

export const getSelectCol = (c: SelectColumn): string =>
  typeof c === 'string' ? c : c.col

export const getSelectAlias = (c: SelectColumn): string =>
  typeof c === 'string' ? c : (c.alias ?? c.col)

export const makeSelectColumn = (
  col: string,
  alias?: string
): SelectColumnConfig => ({
  col,
  alias: alias && alias.trim() !== '' ? alias : col
})

export const isValidAlias = (alias: string): boolean =>
  alias === '' || ALIAS_REGEX.test(alias)

export const isOperationValid = (node: BlueprintNode): boolean => {
  if (node.type === 'filter') {
    return (
      node.conditions.length >= 1 &&
      node.conditions.every(
        (cond) =>
          cond.col !== '' && cond.boundary !== undefined && cond.boundary !== ''
      )
    )
  }
  if (node.type === 'select') {
    return (
      node.columns.length >= 1 &&
      node.columns.every(
        (c) => getSelectCol(c) !== '' && isValidAlias(getSelectAlias(c))
      )
    )
  }
  if (node.type === 'group') {
    return (
      node.columns.length >= 1 &&
      node.columns.every((colConfig) => colConfig.col.trim() !== '')
    )
  }

  if (node.type === 'join') {
    return (
      node.joinType !== undefined &&
      node.on.length >= 1 &&
      node.on.every(
        (cond) => cond.left.trim() !== '' && cond.right.trim() !== ''
      )
    )
  }

  if (node.type === 'sort') {
    return node.cols.length >= 1 && node.cols.every((c) => c.col.trim() !== '')
  }

  if (node.type === 'pivot') {
    return (
      node.pivotColumn.trim() !== '' &&
      node.valueColumn.trim() !== '' &&
      node.pivotValues.length >= 1
    )
  }

  return true
}

export const findNodeById = (
  blueprint: BlueprintNode[],
  nodeId: string
): { node: BlueprintNode; isRowRoot: boolean } | null => {
  const search = (node: BlueprintNode): BlueprintNode | null => {
    if (node.nodeId === nodeId) return node
    if (node.type === 'join') {
      return search(node.left) ?? search(node.right)
    }
    if ('source' in node) {
      return search(node.source)
    }
    return null
  }

  for (const root of blueprint) {
    const found = search(root)
    if (found) return { node: found, isRowRoot: root.nodeId === nodeId }
  }
  return null
}

export const buildEmptyOperationNode = (
  type: OperationType,
  source: BlueprintNode,
  rightNeighbor: BlueprintNode | null
): BlueprintNode => {
  const nodeId = crypto.randomUUID()
  switch (type) {
    case 'filter':
      return {
        nodeId,
        type,
        source,
        conditions: [{ col: '', condition: '=', boundary: '' }],
        logic: 'AND'
      }
    case 'select':
      return { nodeId, type, source, columns: [''] }
    case 'group':
      return { nodeId, type, source, columns: [{ col: '' }] }
    case 'sort':
      return { nodeId, type, source, cols: [{ col: '', direction: 'asc' }] }
    case 'pivot':
      return {
        nodeId,
        type,
        source,
        pivotColumn: '',
        valueColumn: '',
        pivotValues: []
      }
    case 'join':
      if (!rightNeighbor) {
        throw new Error(
          'No se puede inicializar un Join sin una fila vecina inferior.'
        )
      }
      return {
        nodeId,
        type,
        left: source,
        right: rightNeighbor,
        joinType: 'INNER',
        on: [{ left: '', right: '' }]
      }
    default: {
      const _exhaustive: never = type
      return _exhaustive
    }
  }
}
